import Vue from 'vue'
import store from '@/config/vuex'
import DynamicYieldService from '@/api/services/_shared/dynamic-yield-service'
import { addScriptToDOM } from '@/helpers/script'

const DEFAULT_COUNTRY = 'us'

const API_URL_TYPES = {
  static: 'static',
  dynamic: 'dynamic',
}

const DELAYED_CALLBACK_TIMEOUT = 6000

const INVALID_RECOMMENDED_SKU_TYPES = {
  notDefined: 'Undefined',
  emptyArray: 'Empty array',
  invalidFormat: 'Invalid format',
  unknown: 'Unknown',
}

const getApiUrl = (apiId, type) => {
  if (!apiId) return
  if (!Object.values(API_URL_TYPES).includes(type)) return
  return `//cdn.dynamicyield.com/api/${apiId}/api_${type}.js`
}

const addDyScriptsToDOM = dyApiId => {
  return Promise.all([
    addScriptToDOM(getApiUrl(dyApiId, API_URL_TYPES.dynamic), {
      targetEl: document.head,
    }),
    addScriptToDOM(getApiUrl(dyApiId, API_URL_TYPES.static), {
      targetEl: document.head,
    }),
  ])
}

const getHashedEmail = email => window.forge_sha256(email)

const getStoreCurrency = () => document.body.getAttribute('data-currency')

const getCountryLocale = () => {
  const localeMatch = window.location.pathname.match(/^\/([a-z]{2})-([a-z]{2})\//)
  return localeMatch ? localeMatch[2] : DEFAULT_COUNTRY
}

const allSkusAreValid = skus => skus.every(sku => /^\d+\.\d+$/.test(sku))

const sendEventsToDy = events => {
  events.forEach(e => DY.API('event', e))
  DynamicYieldService.reportEvents(events)
}

const getLoginEventObject = email => {
  return {
    name: 'Login',
    properties: {
      dyType: 'login-v1',
      hashedEmail: getHashedEmail(email),
    },
  }
}

const getFormattedCart = (items, factorToPay) => {
  return items.map(item => getFormattedItem(item, factorToPay))
}

const getFormattedItem = (item, factorToPay = 1) => {
  return {
    productId: item.productSku || item.sku,
    quantity: parseInt(item.quantity),
    itemPrice: Number((item.price * factorToPay).toFixed(2)),
  }
}

const DynamicYieldClient = class {
  constructor(defaultPageContext) {
    // make DynamicYieldClient a singleton
    if (!DynamicYieldClient.instance) {
      this.defaultPageContext = { ...defaultPageContext }
      this.pageContext = { ...this.defaultPageContext }
      this.cartItemTotal = null
      this.shouldUpdateRecommendation = true
      this.categoryValues = []
      this.isDyScriptLoadedPromise = addDyScriptsToDOM(process.env.DY_CLIENT_SIDE_API_ID)
      this.isDyScriptLoadedPromise.catch(error => {
        Vue.rollbar.error('Adding dynamicyield scripts failed.', { error })
      })
      DynamicYieldClient.instance = this
    }
    return DynamicYieldClient.instance
  }

  async waitUntilScriptsAreLoaded() {
    await this.isDyScriptLoadedPromise
  }

  onCartUpdate(e) {
    const items = store.state.order.items
    this.cartItemTotal = store.getters['order/itemTotal']

    if (e.detail?.addToCart) {
      this.shouldUpdateRecommendation = true
      this.updateRecommendation(items.map(item => item.sku))
    } else {
      this.shouldUpdateRecommendation = false
      this.trackSyncCart(
        items.map(item => {
          return {
            productSku: item.sku,
            quantity: item.quantity,
            price: item.price,
          }
        })
      )
      this.refreshCampaigns()
    }
  }

  /**
   * @function delayedCallback
   * Run a callback after a delay to check if what was expected has happened. For instance, we can
   * check if we got back recommended skus from DY and fallback to our own recommendation engine
   * if needed.
   *
   * @param {function} callback
   * @param {number} timeoutDelay - Delay before running the callback function
   */
  delayedCallback(callback, timeoutDelay = DELAYED_CALLBACK_TIMEOUT) {
    const timeout = setTimeout(callback, timeoutDelay)

    // If the DY scripts were not loaded, no need to wait for DY response
    this.waitUntilScriptsAreLoaded().catch(() => {
      clearTimeout(timeout)
      callback()
    })
  }

  arrayOfSkusIsValid(skus) {
    return skus && skus.length && allSkusAreValid(skus)
  }

  async trackInvalidRecommendedSkus(experimentInfo, skus) {
    await this.waitUntilScriptsAreLoaded()

    const errorType = !skus
      ? INVALID_RECOMMENDED_SKU_TYPES.notDefined
      : !skus.length
      ? INVALID_RECOMMENDED_SKU_TYPES.emptyArray
      : !allSkusAreValid(skus)
      ? `${INVALID_RECOMMENDED_SKU_TYPES.invalidFormat}: ${JSON.stringify(skus)}`
      : INVALID_RECOMMENDED_SKU_TYPES.unknown

    DY.API('event', {
      name: 'Invalid recommended skus',
      properties: { ...experimentInfo, errorType },
    })
  }

  async trackAddToCart(items, item) {
    await this.waitUntilScriptsAreLoaded()

    let addedItem = item

    if (typeof addedItem === 'string') {
      addedItem = items.find(item => item.productSku === addedItem)
    }
    sendEventsToDy([
      {
        name: 'Add to Cart',
        properties: {
          dyType: 'add-to-cart-v1',
          value: parseFloat(addedItem.price),
          currency: getStoreCurrency(),
          productId: addedItem.productSku,
          quantity: parseInt(addedItem.quantity),
          cart: getFormattedCart(items),
        },
      },
    ])
  }

  async trackMinicartAddToCart(addedItem) {
    await this.waitUntilScriptsAreLoaded()

    sendEventsToDy([
      {
        name: 'Add to Cart Minicart',
        properties: getFormattedItem(addedItem),
      },
    ])
  }

  async trackSyncCart(items) {
    await this.waitUntilScriptsAreLoaded()

    sendEventsToDy([
      {
        name: 'Sync cart',
        properties: {
          dyType: 'sync-cart-v1',
          currency: getStoreCurrency(),
          cart: getFormattedCart(items),
        },
      },
    ])
  }

  trackUserComingFromEmail() {
    const urlParamMatch = window.location.search.match(new RegExp('(^|[?&])uid=([^&]+)'))
    if (urlParamMatch) {
      const email = decodeURIComponent(urlParamMatch[2])
      if (On.helpers.validateEmail(email)) this.identifyUser(email)
    }
  }

  async trackAccountSignup(email) {
    await this.waitUntilScriptsAreLoaded()

    sendEventsToDy([
      {
        name: 'Signup',
        properties: {
          dyType: 'signup-v1',
          hashedEmail: getHashedEmail(email),
        },
      },
    ])
  }

  async trackAccountLogin(email) {
    await this.waitUntilScriptsAreLoaded()

    sendEventsToDy([getLoginEventObject(email)])
  }

  async trackNewsletterSubscription(email) {
    await this.waitUntilScriptsAreLoaded()

    sendEventsToDy([
      {
        name: 'Newsletter Subscription',
        properties: {
          dyType: 'newsletter-subscription-v1',
          hashedEmail: getHashedEmail(email),
        },
      },
    ])
  }

  async identifyUser(email) {
    await this.waitUntilScriptsAreLoaded()

    DY.API('identify', {
      uid: getHashedEmail(email),
      type: 'he',
    })
    DynamicYieldService.reportEvents([getLoginEventObject(email)])
  }

  updateHistoryState() {
    const state = Object.assign({}, window.history.state, {
      dyPageContext: this.pageContext,
    })
    // prevent auto scrolling
    if (window.history.scrollRestoration) window.history.scrollRestoration = 'manual'
    window.history.replaceState(state, '', window.location.href)
  }

  async updatePageContext(restoringPage, countAsPageview = true) {
    await this.waitUntilScriptsAreLoaded()

    if (restoringPage) {
      this.pageContext = window.history.state.dyPageContext || { ...this.defaultPageContext }
    }
    this.refreshCampaigns(countAsPageview)
    if (!restoringPage) this.updateHistoryState()
  }

  async updateRecommendation(skus) {
    await this.waitUntilScriptsAreLoaded()

    const cartContext = { ...this.defaultPageContext, type: 'CART', data: skus }
    DY.recommendationContext = cartContext
    DY.API('spa', {
      context: cartContext,
      countAsPageview: false,
    })
  }

  resetRecommendationContext() {
    DY.recommendationContext = this.pageContext
  }

  async refreshCampaigns(countAsPageview = false) {
    await this.waitUntilScriptsAreLoaded()

    DY.API('spa', {
      context: this.pageContext,
      url: window.location.href,
      countAsPageview,
    })
  }

  reportPageview({ pageContext, cartItemTotal, categoryValues }) {
    this.pageContext = pageContext
    this.cartItemTotal = cartItemTotal
    this.categoryValues = categoryValues
    const { type, data } = this.pageContext
    DynamicYieldService.reportPageview(type, data)
  }
}

const dynamicYieldClient = new DynamicYieldClient({ lng: getCountryLocale(), type: 'OTHER' })
Object.seal(dynamicYieldClient)

// support legacy calls in backend code
window.On = window.On || {}
window.On.DynamicYield = {
  trackNewsletterSubscription: dynamicYieldClient.trackNewsletterSubscription.bind(
    dynamicYieldClient
  ),
  reportPageview: dynamicYieldClient.reportPageview.bind(dynamicYieldClient),
}
window.dispatchEvent(new CustomEvent('on:dy-client-ready'))

export const DynamicYieldClientPlugin = {
  install(Vue) {
    Vue.$dyClient = dynamicYieldClient
    Vue.prototype.$dyClient = dynamicYieldClient
  },
}

export default dynamicYieldClient
